import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { getAuthToken } from '../services/api';

// Georgia 811 locate tickets for one job. Sits above the job tabs so the crew sees it
// before heading out. The status is judged against the job's scheduled date (when it's
// today or later), because that's the day that has to be legal — see locate_summary()
// in app.py.

const LEVEL_STYLE = {
  green: { bg: '#ecfdf5', border: '#a7f3d0', color: '#047857', dot: '#10b981' },
  yellow: { bg: '#fffbeb', border: '#fde68a', color: '#92400e', dot: '#f59e0b' },
  red: { bg: '#fef2f2', border: '#fecaca', color: '#b91c1c', dot: '#ef4444' },
  none: { bg: '#f8fafc', border: '#e2e8f0', color: '#64748b', dot: '#cbd5e1' },
};

const RESPONSE_COLOR = {
  Pending: '#b45309', Marked: '#047857', Clear: '#047857', Conflict: '#b91c1c', Other: '#475569',
};

const authConfig = async () => {
  const token = await getAuthToken();
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Date-only strings: parse as local dates. Never append 'Z' (that shifts the day).
const parseDay = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const fmtDay = (s) => (s ? parseDay(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const addBusinessDays = (d, n) => {
  const out = new Date(d);
  while (n > 0) {
    out.setDate(out.getDate() + 1);
    if (out.getDay() !== 0 && out.getDay() !== 6) n -= 1;
  }
  return out;
};

function blankForm(lifeDays) {
  const today = new Date();
  const start = addBusinessDays(today, 3);
  const exp = new Date(today); exp.setDate(exp.getDate() + (lifeDays || 30));
  return {
    ticket_number: '', ticket_type: 'Normal',
    requested_date: toISO(today), legal_start_date: toISO(start), expiration_date: toISO(exp),
    responses: [{ utility: '', response: 'Pending', note: '' }], notes: '', status: 'Active',
  };
}

export default function LocateTickets({ jobId, user, onChanged }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState(null);      // null = closed; {id?} = editing
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSchedule = !!(user && (user.can_manage_payroll || user.can_manage_schedule));

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/jobs/${jobId}/locate-tickets`, await authConfig());
      setData(res.data);
    } catch (e) {
      console.error('Failed to load 811 tickets', e);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const { summary, tickets } = data;

  // Nothing to say on jobs that don't need 811 and have no tickets — just a quiet
  // affordance for schedulers to flag the job.
  if (!summary.level && tickets.length === 0 && !summary.required) {
    if (!canSchedule) return null;
    return (
      <div style={{ margin: '0 0 10px', fontSize: 12.5, color: '#94a3b8' }}>
        Georgia 811 not required for this job ·{' '}
        <button onClick={() => setRequired(true)} style={linkBtn}>Mark as needing 811</button>
      </div>
    );
  }

  async function setRequired(value) {
    try {
      await axios.put(`${API_URL}/jobs/${jobId}`, { requires_811: value }, await authConfig());
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to update');
    }
  }

  const afterSave = (res) => {
    setData(res.data);
    if (res.data.warning) alert(`Heads up:\n${res.data.warning}`);
  };

  const save = async () => {
    setError('');
    if (!form.ticket_number.trim()) { setError('Ticket number is required'); return; }
    setSaving(true);
    try {
      const body = { ...form, responses: form.responses.filter(r => r.utility.trim()) };
      const cfg = await authConfig();
      const res = form.id
        ? await axios.put(`${API_URL}/locate-tickets/${form.id}`, body, cfg)
        : await axios.post(`${API_URL}/jobs/${jobId}/locate-tickets`, body, cfg);
      afterSave(res);
      setForm(null);
      setExpanded(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save ticket');
    } finally {
      setSaving(false);
    }
  };

  const quickUpdate = async (t, patch) => {
    try {
      afterSave(await axios.put(`${API_URL}/locate-tickets/${t.id}`, patch, await authConfig()));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to update ticket');
    }
  };

  const verify = async (t, undo) => {
    try {
      const res = await axios.post(`${API_URL}/locate-tickets/${t.id}/verify-marks`, { undo }, await authConfig());
      setData(res.data);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to update');
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete 811 ticket #${t.ticket_number}? Use this only for a ticket entered by mistake — otherwise mark it Cancelled to keep the record.`)) return;
    try {
      const res = await axios.delete(`${API_URL}/locate-tickets/${t.id}`, await authConfig());
      setData(res.data);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete ticket');
    }
  };

  const st = LEVEL_STYLE[summary.level || 'none'];
  // Required but no colour = field work is over (the server stays quiet then, since a
  // missing ticket is no longer actionable). Still show it grey so it can be recorded.
  const headline = summary.label
    || (tickets.length ? '811 tickets on file' : 'Georgia 811 · no ticket on file (field work done)');

  return (
    <div style={{ margin: '0 0 12px', border: `1px solid ${st.border}`, background: st.bg, borderRadius: 10 }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', flexWrap: 'wrap' }}
      >
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: st.dot, flexShrink: 0 }} />
        <span style={{ fontWeight: 700, color: st.color, fontSize: 14 }}>{headline}</span>
        {summary.checked_when === 'that day' && (
          <span style={{ fontSize: 12, color: '#64748b' }}>for the scheduled date {fmtDay(summary.checked_for)}</span>
        )}
        {summary.state === 'ok' && summary.expiration_date && (
          <span style={{ fontSize: 12, color: '#64748b' }}>· expires {fmtDay(summary.expiration_date)}</span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b' }}>
          {tickets.length} ticket{tickets.length === 1 ? '' : 's'} {expanded ? '▴' : '▾'}
        </span>
      </div>

      {expanded && (
        <div style={{ padding: '0 14px 12px', fontSize: 13, color: '#334155' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10, color: '#64748b', fontSize: 12.5 }}>
            <span>
              {summary.required ? 'Required' : 'Not required'}
              {summary.required_reason ? ` — ${summary.required_reason}` : ''}
            </span>
            {canSchedule && (
              <select
                value={summary.override === null || summary.override === undefined ? 'auto' : (summary.override ? 'yes' : 'no')}
                onChange={(e) => setRequired(e.target.value === 'auto' ? null : e.target.value === 'yes')}
                style={{ fontSize: 12, padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: 4, background: 'white' }}
              >
                <option value="auto">Auto (by job type / client)</option>
                <option value="yes">Always needs 811</option>
                <option value="no">Doesn't need 811</option>
              </select>
            )}
            <a href="https://georgia811.com" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>
              Open Georgia 811 ↗
            </a>
            <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }}
              onClick={() => { setError(''); setForm(blankForm(data.life_days)); }}>
              + Add ticket
            </button>
          </div>

          {tickets.length === 0 && <div style={{ color: '#64748b' }}>No tickets on file yet.</div>}

          {tickets.map(t => {
            const pending = t.responses.filter(r => r.response === 'Pending').length;
            const cancelled = t.status === 'Cancelled';
            return (
              <div key={t.id} style={{
                background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px',
                marginBottom: 8, opacity: cancelled ? 0.6 : 1,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 14 }}>#{t.ticket_number}</strong>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{t.ticket_type}</span>
                  {cancelled && <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>CANCELLED</span>}
                  {!cancelled && pending > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>{pending} PENDING</span>
                  )}
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm" onClick={() => { setError(''); setForm({ ...t, responses: t.responses.length ? t.responses : [{ utility: '', response: 'Pending', note: '' }] }); }}>Edit</button>
                    {!cancelled
                      ? <button className="btn btn-sm" onClick={() => quickUpdate(t, { status: 'Cancelled' })}>Cancel ticket</button>
                      : <button className="btn btn-sm" onClick={() => quickUpdate(t, { status: 'Active' })}>Reactivate</button>}
                    <button className="btn btn-sm" onClick={() => remove(t)} title="Delete (mistakes only)">✕</button>
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4, fontSize: 12.5, color: '#475569' }}>
                  <span>Requested {fmtDay(t.requested_date)}</span>
                  <span>Legal start <strong>{fmtDay(t.legal_start_date)}</strong></span>
                  <span>Expires <strong>{fmtDay(t.expiration_date)}</strong></span>
                </div>
                {t.responses.length > 0 && (
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {t.responses.map((r, i) => (
                      <span key={i} title={r.note || ''} style={{
                        fontSize: 12, padding: '2px 8px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#f8fafc',
                      }}>
                        {r.utility}: <strong style={{ color: RESPONSE_COLOR[r.response] || '#475569' }}>{r.response}</strong>
                      </span>
                    ))}
                  </div>
                )}
                {t.notes && <div style={{ marginTop: 6, fontSize: 12.5, color: '#475569', whiteSpace: 'pre-wrap' }}>{t.notes}</div>}
                {!cancelled && (
                  <div style={{ marginTop: 8, fontSize: 12.5 }}>
                    {t.marks_verified_at ? (
                      <span style={{ color: '#047857' }}>
                        ✓ Marks verified on site by {t.marks_verified_by_name || 'crew'} ·{' '}
                        {new Date(t.marks_verified_at + 'Z').toLocaleDateString()}{' '}
                        <button onClick={() => verify(t, true)} style={linkBtn}>undo</button>
                      </span>
                    ) : (
                      <button className="btn btn-sm" onClick={() => verify(t, false)}>
                        ✓ Marks verified on site
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {form && (
        <div onClick={() => !saving && setForm(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white', borderRadius: 14, padding: 22, width: '100%', maxWidth: 520,
            maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>{form.id ? 'Edit' : 'Add'} Georgia 811 ticket</h2>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
              Copy the dates from the ticket confirmation — they're pre-filled from the usual rules
              (start 3 business days out, good for {data.life_days} days), but the ticket wins.
            </p>
            <div style={grid2}>
              <Field label="Ticket number *">
                <input value={form.ticket_number} autoFocus onChange={(e) => setForm({ ...form, ticket_number: e.target.value })} style={input} />
              </Field>
              <Field label="Type">
                <select value={form.ticket_type} onChange={(e) => setForm({ ...form, ticket_type: e.target.value })} style={input}>
                  {data.ticket_types.map(tt => <option key={tt} value={tt}>{tt}</option>)}
                </select>
              </Field>
              <Field label="Requested">
                <input type="date" value={form.requested_date || ''} onChange={(e) => setForm({ ...form, requested_date: e.target.value })} style={input} />
              </Field>
              <Field label="Legal start (dig on/after)">
                <input type="date" value={form.legal_start_date || ''} onChange={(e) => setForm({ ...form, legal_start_date: e.target.value })} style={input} />
              </Field>
              <Field label="Expires">
                <input type="date" value={form.expiration_date || ''} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} style={input} />
              </Field>
            </div>

            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: '10px 0 6px' }}>Utility responses</div>
            {form.responses.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input placeholder="Utility (e.g. Georgia Power)" value={r.utility}
                  onChange={(e) => setForm({ ...form, responses: form.responses.map((x, j) => j === i ? { ...x, utility: e.target.value } : x) })}
                  style={{ ...input, flex: 2 }} />
                <select value={r.response}
                  onChange={(e) => setForm({ ...form, responses: form.responses.map((x, j) => j === i ? { ...x, response: e.target.value } : x) })}
                  style={{ ...input, flex: 1 }}>
                  {data.response_values.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <button className="btn btn-sm" onClick={() => setForm({ ...form, responses: form.responses.filter((_, j) => j !== i) })}>✕</button>
              </div>
            ))}
            <button style={linkBtn} onClick={() => setForm({ ...form, responses: [...form.responses, { utility: '', response: 'Pending', note: '' }] })}>
              + Add utility
            </button>

            <Field label="Notes">
              <textarea value={form.notes || ''} rows={2} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ ...input, resize: 'vertical' }} />
            </Field>

            {error && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
              <button className="btn" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save ticket'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'block', marginTop: 8 }}>
      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#374151', marginBottom: 3 }}>{label}</span>
      {children}
    </label>
  );
}

const input = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 14, boxSizing: 'border-box' };
const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', columnGap: 12 };
const linkBtn = { background: 'none', border: 'none', padding: 0, color: '#2563eb', cursor: 'pointer', fontSize: 12.5 };
